// Timeout-by-default wrapper for Supabase reads/writes from hooks and components.
//
// Why this exists: every "stuck until app restart" incident (dashboard skeletons,
// referral button, Pro cards, deep links) traced back to a Supabase call with no
// timeout wedging a loading state. This helper makes the guard the default so new
// call sites can't reintroduce the bug class.
//
// Usage:
//   const { data, error } = await readWithTimeout(
//     signal => supabase.from('watch_zones').select('...').eq(...).abortSignal(signal),
//     { label: 'watch_zones_load' }
//   );
//
// Contract:
// - Always resolves to a PostgREST-shaped `{ data, error }` — never throws, never hangs.
// - On timeout/abort, `error.message` is `"<label> timed out after <ms>ms"` and
//   `error.isTimeout` is true so callers can branch without string matching.
// - Does NOT touch the shared Supabase circuit breaker: hook/background reads must
//   not open it (see docs/pro-intelligence-hang-diagnosis-2026-06-10.md addendum).

export const DEFAULT_READ_TIMEOUT_MS = 8000;

export async function readWithTimeout(buildQuery, { timeoutMs = DEFAULT_READ_TIMEOUT_MS, label = 'supabase_read' } = {}) {
  const controller = new AbortController();
  let timer;
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => {
      resolve({ data: null, error: Object.assign(new Error(`${label} timed out after ${timeoutMs}ms`), { isTimeout: true }) });
      controller.abort();
    }, timeoutMs);
  });
  try {
    const result = await Promise.race([Promise.resolve().then(() => buildQuery(controller.signal)), timeout]);
    if (result && typeof result === 'object' && ('data' in result || 'error' in result)) {
      return result;
    }
    return { data: result ?? null, error: null };
  } catch (err) {
    const aborted = err?.name === 'AbortError' || controller.signal.aborted;
    const error = aborted
      ? Object.assign(new Error(`${label} timed out after ${timeoutMs}ms`), { isTimeout: true })
      : err;
    if (aborted) console.warn(`[supabaseRead] ${label} timed out after ${timeoutMs}ms`);
    return { data: null, error };
  } finally {
    clearTimeout(timer);
  }
}

// For promises that don't support .abortSignal (e.g. supabase.auth.*): resolves
// to `{ data, error }` with the same timeout error shape. The underlying request
// is not cancelled — we just stop waiting so UI loading states can settle.
export async function settleWithTimeout(promise, { timeoutMs = DEFAULT_READ_TIMEOUT_MS, label = 'supabase_call' } = {}) {
  let timer;
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => resolve({
      data: null,
      error: Object.assign(new Error(`${label} timed out after ${timeoutMs}ms`), { isTimeout: true }),
    }), timeoutMs);
  });
  try {
    const result = await Promise.race([
      Promise.resolve(promise).then(
        r => (r && typeof r === 'object' && ('data' in r || 'error' in r)) ? r : { data: r ?? null, error: null },
        err => ({ data: null, error: err })
      ),
      timeout,
    ]);
    if (result?.error?.isTimeout) console.warn(`[supabaseRead] ${label} timed out after ${timeoutMs}ms`);
    return result;
  } finally {
    clearTimeout(timer);
  }
}
